import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Send, Eye, AlertTriangle, RotateCcw } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Card, Modal } from "@/components/cc";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { CONTENT_NEXT } from "@/lib/transitions";

const REGIONS = ["ghana", "africa", "diaspora", "united_states", "global"];

export default function ContentEditor() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [cats, setCats] = useState([]);
  const [sources, setSources] = useState([]);
  const [claims, setClaims] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [corrModal, setCorrModal] = useState(false);
  const [corr, setCorr] = useState({ kind: "material", explanation: "" });
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes("super_admin");
  const [markModal, setMarkModal] = useState(false);
  const [unmarkModal, setUnmarkModal] = useState(false);
  const [markBusy, setMarkBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/content/${id}`).then((r) => setC(r.data)).catch(() => {});
    api.get(`/content/${id}/revisions`).then((r) => setRevisions(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/sources").then((r) => setSources(r.data)).catch(() => {});
    api.get("/claims").then((r) => setClaims(r.data)).catch(() => {});
  }, [id, load]);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      await api.patch(`/content/${id}`, {
        title: c.title, subtitle: c.subtitle, summary: c.summary, body: c.body,
        region: c.region, category_id: c.category_id || null, editorial_notes: c.editorial_notes,
        source_ids: c.source_ids, claim_ids: c.claim_ids, primary_claim_id: c.primary_claim_id || null,
      });
      toast.success("Saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const transition = async (target) => {
    try { await api.post(`/content/${id}/transition`, { target }); toast.success(`Moved to ${target}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const publish = async () => {
    try { await api.post(`/content/${id}/publish`, {}); toast.success("Published"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const unpublish = async () => {
    try { await api.post(`/content/${id}/unpublish`); toast.success("Unpublished"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const restore = async () => {
    try { await api.post(`/content/${id}/restore`); toast.success("Restored to draft"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setTestMarker = async (isTest) => {
    if (markBusy) return;
    setMarkBusy(true);
    try {
      await api.post(`/content/${id}/test-marker`, { is_test: isTest });
      toast.success(isTest ? "Marked as TEST / QA" : "TEST / QA marker removed");
      setMarkModal(false); setUnmarkModal(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMarkBusy(false);
    }
  };

  const preview = async () => {
    try {
      const { data } = await api.post(`/content/${id}/preview-token`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/preview/${data.token}`, "_blank");
    } catch (err) { toast.error("Preview unavailable"); }
  };

  const submitCorrection = async (e) => {
    e.preventDefault();
    try { await api.post(`/content/${id}/correction`, corr); toast.success("Correction recorded"); setCorrModal(false); setCorr({ kind: "material", explanation: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggle = (key, val) => {
    const cur = c[key] || [];
    set(key, cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  };

  if (!c) return <div className="p-6 text-zinc-500">Loading…</div>;
  const next = CONTENT_NEXT[c.status] || [];
  const isFact = c.type === "fact_check";

  return (
    <div className="p-6">
      <Link to="/command-center/studio" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Studio</Link>
      <PageHeader title={c.title || "Untitled"} subtitle={`${c.type.replace("_", "-")} · rev ${c.version}`}>
        <StatusBadge status={c.status} />
        {c.is_test_record && (
          <span data-testid="content-test-badge" className="text-[0.6rem] font-bold uppercase tracking-wide bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2 py-1 rounded-sm">TEST / QA</span>
        )}
        <Btn variant="ghost" data-testid="preview-btn" onClick={preview}><Eye className="h-4 w-4" /> Preview</Btn>
        <Btn data-testid="save-content-btn" onClick={save}><Save className="h-4 w-4" /> Save</Btn>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Editor */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-4 space-y-3">
            <Field label="Title"><input data-testid="edit-title" className={inputCls} value={c.title} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field label="Subtitle"><input className={inputCls} value={c.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} /></Field>
            <Field label="Summary"><textarea className={inputCls} rows={2} value={c.summary || ""} onChange={(e) => set("summary", e.target.value)} /></Field>
            <Field label="Body (HTML supported)"><textarea data-testid="edit-body" className={`${inputCls} font-mono-data`} rows={12} value={c.body || ""} onChange={(e) => set("body", e.target.value)} /></Field>
          </Card>

          {isFact && (
            <Card className="p-4">
              <div className="label-caps text-zinc-500 mb-2">Primary Claim (drives verdict)</div>
              <select data-testid="primary-claim-select" className={inputCls} value={c.primary_claim_id || ""} onChange={(e) => set("primary_claim_id", e.target.value)}>
                <option value="">— select claim —</option>
                {claims.map((cl) => <option key={cl.id} value={cl.id}>{cl.statement.slice(0, 70)}{cl.verdict ? ` [${cl.verdict}]` : ""}</option>)}
              </select>
            </Card>
          )}

          <Card className="p-4">
            <div className="label-caps text-zinc-500 mb-2">Editorial Notes (never public)</div>
            <textarea className={inputCls} rows={2} value={c.editorial_notes || ""} onChange={(e) => set("editorial_notes", e.target.value)} />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="p-4">
            <div className="label-caps text-zinc-500 mb-3">Workflow</div>
            <div className="flex flex-wrap gap-2">
              {next.map((t) => <Btn key={t} variant="ghost" data-testid={`content-transition-${t}`} onClick={() => transition(t)}>{t.replace(/_/g, " ")}</Btn>)}
            </div>
            {c.is_test_record && (
              <p data-testid="content-test-publish-notice" className="mt-3 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-sm p-2">
                This is an automated test / QA record. It is private and cannot be published to the public website.
              </p>
            )}
            {!c.is_test_record && ["ready_to_publish", "updated"].includes(c.status) && (
              <Btn data-testid="publish-btn" onClick={publish} className="w-full justify-center mt-3"><Send className="h-4 w-4" /> Publish</Btn>
            )}
            {["rejected", "archived"].includes(c.status) && (
              <Btn variant="ghost" data-testid="restore-btn" onClick={restore} className="w-full justify-center mt-3">Restore to Draft</Btn>
            )}
            {["published", "updated"].includes(c.status) && (
              <>
                <Btn variant="danger" data-testid="unpublish-btn" onClick={unpublish} className="w-full justify-center mt-2">Unpublish</Btn>
                <Btn variant="ghost" data-testid="correction-btn" onClick={() => setCorrModal(true)} className="w-full justify-center mt-2"><AlertTriangle className="h-4 w-4" /> Add Correction</Btn>
                {c.status === "published" && (
                  <Link to={`/${c.type.replace("_", "-")}/${c.slug}`} target="_blank" className="mt-3 block text-center text-xs text-emerald-400">View live →</Link>
                )}
              </>
            )}
          </Card>

          <Card className="p-4">
            <div className="label-caps text-zinc-500 mb-2">Metadata</div>
            <Field label="Region">
              <select className={inputCls} value={c.region} onChange={(e) => set("region", e.target.value)}>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <div className="mt-3">
              <Field label="Category">
                <select className={inputCls} value={c.category_id || ""} onChange={(e) => set("category_id", e.target.value)}>
                  <option value="">— none —</option>
                  {cats.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </Field>
            </div>
          </Card>

          <Card className="p-4">
            <div className="label-caps text-zinc-500 mb-2">Sources ({c.source_ids?.length || 0})</div>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {sources.map((s) => (
                <button key={s.id} onClick={() => toggle("source_ids", s.id)}
                  className={`text-xs px-2 py-1 rounded-sm border ${c.source_ids?.includes(s.id) ? "bg-emerald-600 border-emerald-500 text-white" : "border-zinc-700 text-zinc-400"}`}>
                  {s.is_private ? "🔒 " : ""}{s.title.slice(0, 24)}
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-4">
            <div className="label-caps text-zinc-500 mb-2">Claims ({c.claim_ids?.length || 0})</div>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {claims.map((cl) => (
                <button key={cl.id} onClick={() => toggle("claim_ids", cl.id)}
                  className={`text-xs px-2 py-1 rounded-sm border text-left ${c.claim_ids?.includes(cl.id) ? "bg-emerald-600 border-emerald-500 text-white" : "border-zinc-700 text-zinc-400"}`}>
                  {cl.statement.slice(0, 30)}
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-4">
            <div className="label-caps text-zinc-500 mb-2 flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5" /> Revision History</div>
            <div className="space-y-1 text-xs font-mono-data max-h-40 overflow-y-auto">
              {revisions.length === 0 && <p className="text-zinc-600">No prior revisions.</p>}
              {revisions.map((r) => (
                <div key={r.id} className="flex justify-between text-zinc-400">
                  <span>rev {r.revision_number} · {r.kind}</span>
                  <span className="text-zinc-600">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </Card>

          {isAdmin && (
            <Card className="p-4 border-amber-500/30" data-testid="content-qa-safety-panel">
              <div className="label-caps text-amber-400/80 mb-2 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" /> QA Safety · Record Classification</div>
              {!c.is_test_record ? (
                <>
                  <p className="text-xs text-zinc-500 mb-3">Marking this record as TEST / QA keeps it private and blocks it from ever being published.</p>
                  <Btn variant="ghost" data-testid="content-mark-test-btn" onClick={() => setMarkModal(true)} className="w-full justify-center border-amber-500/40 text-amber-300">Mark as TEST / QA</Btn>
                </>
              ) : (
                <>
                  <p className="text-xs text-amber-300/80 mb-3">This record is classified TEST / QA and cannot be published.</p>
                  <button data-testid="content-unmark-test-btn" onClick={() => setUnmarkModal(true)} className="w-full text-center text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2 py-2">Remove TEST / QA marker (advanced)</button>
                </>
              )}
            </Card>
          )}
        </div>
      </div>

      <Modal open={markModal} onClose={() => { if (!markBusy) setMarkModal(false); }} title="Mark as TEST / QA?">
        <p className="text-sm text-zinc-400" data-testid="content-mark-explanation">This record will remain private and cannot be published while marked as TEST / QA.</p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" data-testid="content-mark-cancel" disabled={markBusy} onClick={() => setMarkModal(false)}>Cancel</Btn>
          <Btn data-testid="content-mark-confirm" disabled={markBusy} onClick={() => setTestMarker(true)}>{markBusy ? "Working…" : "Confirm"}</Btn>
        </div>
      </Modal>

      <Modal open={unmarkModal} onClose={() => { if (!markBusy) setUnmarkModal(false); }} title="Remove TEST / QA marker?">
        <p className="text-sm text-amber-300/90" data-testid="content-unmark-explanation">Warning: removing the TEST / QA marker makes this record eligible for publication again. It will NOT be published automatically — you must publish it deliberately.</p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" data-testid="content-unmark-cancel" disabled={markBusy} onClick={() => setUnmarkModal(false)}>Cancel</Btn>
          <Btn variant="danger" data-testid="content-unmark-confirm" disabled={markBusy} onClick={() => setTestMarker(false)}>{markBusy ? "Working…" : "Remove marker"}</Btn>
        </div>
      </Modal>

      <Modal open={corrModal} onClose={() => setCorrModal(false)} title="Add Correction">
        <form onSubmit={submitCorrection} className="space-y-4">
          <Field label="Type">
            <select className={inputCls} value={corr.kind} onChange={(e) => setCorr({ ...corr, kind: e.target.value })}>
              <option value="material">Material (public, new snapshot)</option>
              <option value="minor">Minor (internal only)</option>
            </select>
          </Field>
          <Field label="Explanation"><textarea data-testid="correction-input" className={inputCls} rows={3} required value={corr.explanation} onChange={(e) => setCorr({ ...corr, explanation: e.target.value })} /></Field>
          <Btn data-testid="correction-submit-btn" type="submit" className="w-full justify-center">Record Correction</Btn>
        </form>
      </Modal>
    </div>
  );
}
